import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Radio,
  Spin,
  Steps,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  MergeCellsOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { useTranslate } from "@refinedev/core";

import {
  MergeStore,
  MergeCandidate,
  MergeChoice,
  MergeFieldChoices,
  MergeFieldConflict,
  MergePreview,
  executeMerge,
  fetchMergePreview,
  fetchMergeStores,
} from "@/services/contact-merge.api";

const { Text, Title } = Typography;

/**
 * Lo mínimo para arrancar la unificación con el origen ya elegido. Los contadores de la lista de
 * candidatos no hacen falta: con el origen puesto el paso 1 se saltea, y el `preview` lo calcula el
 * backend, que además revalida store, permisos y bloqueos — un preset no puede saltear nada.
 */
export interface MergeSourcePreset {
  contactId: number;
  userId: number;
  name: string;
  email: string;
  whatsapp?: string | null;
  isSyntheticEmail?: boolean;
  stores?: string[];
  matchReason?: MergeCandidate["matchReason"];
}

interface Props {
  /** Contacto DESTINO: el que se queda. */
  targetContactId: number;
  targetName: string;
  /** Store de la ficha desde la que se abrió el drawer. */
  targetStoreCode?: string | null;
  /**
   * Origen ya elegido, para cuando el drawer se abre desde el conflicto de identidad de la edición
   * de la ficha (OpenSpec 021 · D12): ahí el duplicado no hay que buscarlo, el operador acaba de
   * tipear el dato del otro contacto. Salta directo al paso 2 (Revisar) — el paso 3 sigue pidiendo
   * la confirmación tipeada, que es la que protege la operación.
   *
   * Viene con los datos del otro contacto, **no** como un id a buscar en la lista de candidatos: el
   * par que dispara este camino todavía **no comparte ninguna clave** (el dato nuevo no se guardó),
   * así que `merge-candidates` no lo devuelve y buscarlo ahí dejaba el drawer en el paso 1.
   */
  initialSource?: MergeSourcePreset | null;
  /**
   * Campo que disparó el conflicto de identidad (D12 / D16). En el paso Revisar llega pre-seleccionado
   * en "source" y deshabilitado: el operador ya eligió ese dato al tipearlo, no tiene sentido volver a
   * preguntarle. El resto de los campos en conflicto siguen siendo elegibles normalmente.
   */
  triggerField?: "email" | "whatsapp" | null;
  /**
   * Se llama al terminar bien, con el `contactId` que **sobrevivió** — que no es necesariamente la
   * ficha desde la que se abrió el drawer: el selector de store puede haber movido el destino a la
   * ficha de esta identidad en otro store.
   */
  onDone: (survivingContactId: number) => void;
  onClose: () => void;
}

/**
 * Fallbacks en español de los diccionarios que vienen del backend por código
 * (`field`, `reason`, `matchReason` y las claves de `counts`). Se traducen con
 * `t("contacts.merge.<grupo>.<code>", fallback)`, así un código nuevo del backend nunca rompe la
 * pantalla: en el peor caso se muestra el código.
 */
const FIELD_FALLBACK: Record<string, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  firstname: "Nombre",
  lastname: "Apellido",
  company: "Empresa",
};

const REASON_FALLBACK: Record<string, string> = {
  synthetic_email: "el otro es un e-mail generado por el alta por WhatsApp",
  synthetic_name: "el otro es el número de teléfono, no un nombre",
  empty_target: "el contacto que se queda no tiene este dato",
  empty_source: "el contacto a absorber no tiene este dato",
  equivalent: "son el mismo valor",
  both_empty: "ninguno tiene este dato",
  target_wins: "por defecto se conserva el del contacto que se queda",
  operator_choice: "elegido por vos",
};


const COUNT_FALLBACK: Record<string, string> = {
  quotes: "Presupuestos",
  orders: "Pedidos",
  ordersGrid: "Pedidos (grid)",
  shipments: "Entregas",
  addresses: "Direcciones",
  adminAddresses: "Direcciones (admin)",
  groups: "Grupos",
  relations: "Relaciones",
  relationsReverse: "Relaciones inversas",
  participations: "Participaciones",
  contactSellers: "Sellers asignados",
  vendorLinks: "Vínculos con vendors",
  wishlists: "Favoritos",
  rewardHistory: "Historial de puntos",
  rewardBalance: "Saldo de puntos",
  newsletter: "Newsletter",
  reviews: "Reseñas",
  priceAdjustments: "Ajustes de precio",
  historyLegacy: "Historial",
  conversations: "Conversaciones",
  customerLog: "Registro de accesos",
};

/**
 * OpenSpec 021 — Unificar contacto.
 *
 * Tres pasos, en este orden y sin atajos, porque la acción es **irreversible** (el customer de
 * Magento del contacto absorbido se elimina):
 *
 *   1. Elegir a quién absorber — sugerencias por teléfono/email/nombre + búsqueda libre. Nada
 *      preseleccionado: el duplicado del caso típico (una cuenta con email, otra con WhatsApp)
 *      no comparte ninguna clave y sólo lo puede confirmar una persona.
 *   2. Revisar — qué se traslada, qué campos quedan y qué se pierde.
 *   3. Confirmar — con el nombre tipeado a mano.
 */
export const MergeContactDrawer = ({
  targetContactId,
  targetName,
  targetStoreCode,
  initialSource,
  triggerField,
  onDone,
  onClose,
}: Props) => {
  const t = useTranslate();
  const [step, setStep] = useState(0);

  /**
   * La unificación es **dentro de un mismo store**. El store lo define la ficha destino, así que
   * cuando la identidad tiene ficha en varios, el operador elige en cuál unificar y el destino
   * pasa a ser el contacto de ESE store.
   */
  const [stores, setStores] = useState<MergeStore[]>([]);
  const [activeContactId, setActiveContactId] = useState(targetContactId);

  const [selected, setSelected] = useState<MergeCandidate | null>(null);

  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [choices, setChoices] = useState<MergeFieldChoices>({});

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [executing, setExecuting] = useState(false);
  /** Se genera una vez por intento y se reusa en los reintentos del mismo intento. */
  const [idempotencyKey, setIdempotencyKey] = useState<string>("");

  useEffect(() => {
    fetchMergeStores(targetContactId)
      .then(setStores)
      .catch(() => setStores([]));
  }, [targetContactId]);

  const activeStoreCode =
    stores.find((store) => store.contactId === activeContactId)?.storeCode ??
    targetStoreCode ??
    null;

  const loadPreview = useCallback(
    async (candidate: MergeCandidate, fieldChoices: MergeFieldChoices) => {
      setPreviewLoading(true);
      try {
        const data = await fetchMergePreview(
          activeContactId,
          candidate.contactId,
          fieldChoices,
        );
        setPreview(data);
        return data;
      } catch (error: any) {
        const payload = error?.response?.data;
        if (payload?.code === "MERGE_FORBIDDEN_STORES") {
          message.error(
            t(
              "contacts.merge.error.forbiddenStores",
              "No tenés permiso de Contactos (admin) en estos stores",
            ) + `: ${(payload.stores ?? []).join(", ")}`,
          );
        } else {
          message.error(
            payload?.message ??
              t(
                "contacts.merge.error.preview",
                "No se pudo calcular el plan de unificación",
              ),
          );
        }
        return null;
      } finally {
        setPreviewLoading(false);
      }
    },
    [activeContactId],
  );

  const pickCandidate = async (candidate: MergeCandidate, initialChoices: MergeFieldChoices = {}) => {
    setSelected(candidate);
    setChoices(initialChoices);
    // La clave identifica UN intento sobre UN par. Si se cambia de candidato y se reusa, el
    // backend responde con el resultado del merge anterior (o lo rechaza).
    setIdempotencyKey("");
    const data = await loadPreview(candidate, initialChoices);
    if (data) setStep(0);
  };

  /**
   * Preselección del origen cuando el drawer se abrió desde el conflicto de identidad (D12).
   *
   * Se aplica UNA sola vez y no espera la lista de candidatos: el par de este camino no comparte
   * ninguna clave todavía, así que no aparece ahí. El `preview` que dispara es el que valida.
   */
  // Se compara por identidad del objeto y no con un flag de "ya pasó": la ficha crea un preset nuevo
  // cada vez que el operador acepta el popup, así que un segundo conflicto —incluso con el mismo
  // contacto— vuelve a preseleccionar aunque el drawer haya quedado montado.
  const preselectedFor = useRef<MergeSourcePreset | null>(null);
  useEffect(() => {
    if (!initialSource) return;
    if (preselectedFor.current === initialSource) return;
    preselectedFor.current = initialSource;
    const initialChoices: MergeFieldChoices = triggerField ? { [triggerField]: "source" } : {};
    void pickCandidate({
      contactId: initialSource.contactId,
      userId: initialSource.userId,
      name: initialSource.name,
      email: initialSource.email,
      isSyntheticEmail: !!initialSource.isSyntheticEmail,
      whatsapp: initialSource.whatsapp ?? null,
      stores: initialSource.stores ?? [],
      // El preset viene del popup de conflicto de identidad, que no trae el dato: se deriva de
      // sus stores. Sin `activeStoreCode` todavía resuelto se asume que sí — es el caso normal
      // (el conflicto salta editando una ficha de este store) y evita un aviso falso.
      inTargetStore: activeStoreCode
        ? (initialSource.stores ?? []).includes(activeStoreCode)
        : true,
      quotesCount: 0,
      ordersCount: 0,
      matchReason: initialSource.matchReason ?? "manual",
    }, initialChoices);
  }, [initialSource]);

  const changeChoice = async (field: string, value: MergeChoice) => {
    const next = { ...choices, [field]: value };
    setChoices(next);
    if (selected) await loadPreview(selected, next);
  };

  const run = async () => {
    if (!selected) return;
    const key =
      idempotencyKey ||
      `merge-${activeContactId}-${selected.contactId}-${Date.now()}`;
    setIdempotencyKey(key);
    setExecuting(true);
    try {
      const result = await executeMerge(
        activeContactId,
        selected.contactId,
        key,
        choices,
      );
      if (result.status === "done") {
        message.success(t("contacts.merge.result.ok", "Contactos unificados"));
        onDone(activeContactId);
        onClose();
      } else {
        message.warning(
          `${t(
            "contacts.merge.result.partial",
            "La unificación quedó incompleta",
          )} ` +
            `(${result.status} / ${result.step ?? "-"}). ` +
            t(
              "contacts.merge.result.retry",
              "Se puede reintentar: nada se borró.",
            ),
        );
      }
    } catch (error: any) {
      const payload = error?.response?.data;
      message.error(
        payload?.blockers?.[0]?.message ??
          payload?.message ??
          t(
            "contacts.merge.result.error",
            "La unificación falló. Nada se borró — se puede reintentar.",
          ),
      );
    } finally {
      setExecuting(false);
    }
  };

  const movingCounts = useMemo(
    () =>
      Object.entries(preview?.counts ?? {}).filter(([, value]) => value > 0),
    [preview],
  );
  const realConflicts = useMemo(
    () =>
      (preview?.fieldConflicts ?? []).filter((conflict) => conflict.isConflict),
    [preview],
  );

  const displayConflicts = useMemo(() => {
    const allFields = preview?.fieldConflicts ?? [];
    const fn = realConflicts.find((c) => c.field === "firstname");
    const ln = realConflicts.find((c) => c.field === "lastname");

    const merged: MergeFieldConflict = {
      field: "__name__",
      targetValue:
        [fn?.targetValue, ln?.targetValue].filter(Boolean).join(" ") || null,
      sourceValue:
        [fn?.sourceValue, ln?.sourceValue].filter(Boolean).join(" ") || null,
      default: fn?.default ?? ln?.default ?? "target",
      reason: "",
      isConflict: !!(fn?.isConflict || ln?.isConflict),
    };

    const result: MergeFieldConflict[] = [];
    let nameInserted = false;
    for (const c of realConflicts) {
      if (c.field === "firstname" || c.field === "lastname") {
        if (!nameInserted) { result.push(merged); nameInserted = true; }
      } else {
        result.push(c);
      }
    }
    if (!nameInserted) result.push(merged);

    // Empresa: visible aunque no sea conflicto, pero SÓLO si el backend la mandó.
    //
    // Antes, cuando no venía, se fabricaba una fila vacía acá. Dejó de ser correcto: la Empresa
    // es un dato POR STORE, y el backend la ofrece únicamente cuando las dos fichas son del mismo
    // store. Entre stores distintos no hay nada que elegir —cada ficha se queda con la suya— y la
    // fila fabricada sería un control fantasma: el operador elige y no pasa nada, porque el
    // backend resuelve contra los campos que él mismo armó y ahí no está.
    const companyField = allFields.find((c) => c.field === "company");
    if (companyField && !result.find((c) => c.field === "company")) {
      result.push(companyField);
    }

    const ORDER = ["__name__", "email", "whatsapp", "company"];
    return result.sort((a, b) => {
      const ai = ORDER.indexOf(a.field);
      const bi = ORDER.indexOf(b.field);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [realConflicts, preview]);

  const changeNameChoice = async (value: MergeChoice) => {
    const next = { ...choices, firstname: value, lastname: value };
    setChoices(next);
    if (selected) await loadPreview(selected, next);
  };

  const blocked = (preview?.blockers?.length ?? 0) > 0;

  return (
    <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
      <div style={{ padding: "16px 24px 8px", flexShrink: 0 }}>
        <Steps
          size="small"
          current={step}
          items={[
            { title: t("contacts.merge.step.review", "Revisar") },
            { title: t("contacts.merge.step.confirm", "Confirmar") },
          ]}
        />
      </div>

      {/* ── Paso 1 · Revisar ─────────────────────────────────────────────── */}
      {step === 0 && selected && (
        <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Spin spinning={previewLoading}>
              <Flex vertical gap={16} style={{ padding: 24 }}>
                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    padding: "12px 16px",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <MergeCellsOutlined
                    style={{ fontSize: 18, marginTop: 2, color: "rgba(0,0,0,0.45)" }}
                  />
                  <div>
                    <Text strong>
                      {t(
                        "contacts.merge.review.absorbedInto",
                        "Los datos de {{source}} van a pasar a la ficha de {{target}}",
                      )
                        .replace("{{source}}", selected.name || selected.email || "")
                        .replace("{{target}}", targetName)}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" italic style={{ fontSize: 13 }}>
                        {t(
                          "contacts.merge.review.identityScope",
                          "El e-mail y el WhatsApp se actualizan en todos los stores donde {{source}} exista.",
                        ).replace("{{source}}", selected.name || selected.email || "")}
                      </Text>
                    </div>
                    {/*
                      El otro alcance, el que se llevaba puesta la gente: el nombre y la Empresa
                      son de la ficha. Decirlo en una frase resultó más claro que mostrar el dato
                      crudo de la identidad al lado del de la ficha, que era el plan original.
                    */}
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" italic style={{ fontSize: 13 }}>
                        {t(
                          "contacts.merge.review.contactScope",
                          "El nombre y la Empresa quedan solamente en esta ficha: no cambian en los otros stores.",
                        )}
                      </Text>
                    </div>
                  </div>
                </div>

                {preview?.blockers?.map((blocker) => (
                  <Alert
                    key={blocker.code}
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message={blocker.message}
                  />
                ))}

                {displayConflicts.length > 0 && (
                  <Flex vertical gap={8}>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      {t("contacts.merge.review.conflicts", "Datos a unificar")}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t(
                        "contacts.merge.review.conflictsHelp",
                        "Elegí cuál de los datos serán los definitivos del contacto.",
                      )}
                    </Text>
                    <Flex vertical gap={24}>
                    {displayConflicts.map((conflict) => {
                      const isName = conflict.field === "__name__";
                      const isTrigger = !!triggerField && conflict.field === triggerField;
                      const choiceValue = isName
                        ? (choices.firstname ?? choices.lastname ?? conflict.default)
                        : isTrigger
                          ? "source"
                          : (choices[conflict.field] ?? conflict.default);
                      const label = isName
                        ? t("contacts.merge.field.__name__", "Nombre completo")
                        : t(`contacts.merge.field.${conflict.field}`, FIELD_FALLBACK[conflict.field] ?? conflict.field);
                      return (
                        <Flex key={conflict.field} vertical gap={2}>
                          <Text strong style={{ fontSize: 13 }}>{label}</Text>
                          <Radio.Group
                            value={choiceValue}
                            onChange={(event) =>
                              isName
                                ? void changeNameChoice(event.target.value)
                                : void changeChoice(conflict.field, event.target.value)
                            }
                          >
                            <Flex vertical gap={2}>
                              <Radio value="target" disabled={isTrigger}>
                                {conflict.targetValue || t("contacts.merge.emptyValue", "(vacío)")}
                              </Radio>
                              <Radio value="source">
                                {conflict.sourceValue || t("contacts.merge.emptyValue", "(vacío)")}
                              </Radio>
                            </Flex>
                          </Radio.Group>
                        </Flex>
                      );
                    })}
                    </Flex>
                  </Flex>
                )}


                {(preview?.warnings?.length ?? 0) > 0 && (
                  <Flex vertical gap={6}>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      {t("contacts.merge.review.warnings", "Tené en cuenta")}
                    </Title>
                    {preview!.warnings.map((warning) => (
                      <Alert
                        key={warning.code}
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        message={warning.message}
                      />
                    ))}
                  </Flex>
                )}
              </Flex>
            </Spin>
          </div>

          <Flex
            gap={8}
            justify="space-between"
            style={{
              flexShrink: 0,
              padding: "12px 24px",
              borderTop: "1px solid #f0f0f0",
              background: "#fff",
            }}
          >
            <Button icon={<ArrowLeftOutlined />} onClick={onClose}>
              {t("contacts.merge.back", "Atrás")}
            </Button>
            <Button
              type="primary"
              disabled={blocked}
              onClick={() => setStep(1)}
              icon={<ArrowRightOutlined />}
              iconPosition="end"
            >
              {t("contacts.merge.continue", "Continuar")}
            </Button>
          </Flex>
        </Flex>
      )}

      {/* ── Paso 2 · Confirmar ───────────────────────────────────────────── */}
      {step === 1 && selected && (
        <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Flex vertical gap={24} style={{ padding: 24 }}>
              {/* Resumen de la operación */}
              <div
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <MergeCellsOutlined
                  style={{ fontSize: 18, marginTop: 2, color: "rgba(0,0,0,0.45)" }}
                />
                <div>
                  <Text strong>
                    {t(
                      "contacts.merge.confirm.summary",
                      "Los datos de {{source}} van a pasar a la ficha de {{target}}",
                    )
                      .replace("{{source}}", selected.name || selected.email || "")
                      .replace("{{target}}", targetName)}
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {t(
                        "contacts.merge.confirm.detail",
                        "{{source}} dejará de existir como contacto independiente. La operación queda registrada en el historial.",
                      ).replace("{{source}}", selected.name || selected.email || "")}
                    </Text>
                  </div>
                </div>
              </div>

              <Alert
                type="warning"
                showIcon
                message={t(
                  "contacts.merge.confirm.irreversible",
                  "Esta operación no se puede deshacer. Revisá los datos del paso anterior antes de confirmar.",
                )}
              />
            </Flex>
          </div>

          <Flex
            gap={8}
            justify="space-between"
            style={{
              flexShrink: 0,
              padding: "12px 24px",
              borderTop: "1px solid #f0f0f0",
              background: "#fff",
            }}
          >
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep(0)}
              disabled={executing}
            >
              {t("contacts.merge.back", "Volver")}
            </Button>
            <Button
              type="primary"
              loading={executing}
              disabled={blocked}
              onClick={run}
              icon={<CheckOutlined />}
              iconPosition="end"
            >
              {t("contacts.merge.confirm.submit", "Unificar contactos")}
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
